import React, { useMemo } from "react";
import { ArrowDownAZ, ArrowUpAZ, ArrowUpDown, ChevronDown, Download, ListFilter, RefreshCw, Search, Settings2, Trash2, Contact } from "lucide-react";
import { CollapsibleSearch } from "@/components/ui/collapsible-search";

import InCardFilterPanel from "@/components/leads/InCardFilterPanel";
import ZohoFilterPanel from "@/components/leads/ZohoFilterPanel";

import AppointmentTableColumnsDialog from "@/components/appointments/AppointmentTableColumnsDialog";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const DEFAULT_VISIBLE_FILTERS = ["start_at", "status", "location", "custom_filters"];

const DEFAULT_TABLE_COLUMNS = ["status", "full_name", "start_at", "end_at", "location", "phone_number", "state"];

const APPT_FIELDS = [
  // Core appointment fields
  { key: "start_at", label: "Start Date", type: "date" },
  { key: "end_at", label: "End Date", type: "date" },
  { key: "status", label: "Status", type: "select" },
  { key: "location", label: "Location", type: "text" },
  { key: "title", label: "Title", type: "text" },

  // Lead snapshot fields
  { key: "full_name", label: "Name", type: "text" },
  { key: "phone_number", label: "Phone", type: "text" },
  { key: "state", label: "State", type: "select" },

  // Summary fields
  { key: "appointment_outcome", label: "Outcome", type: "select" },
  { key: "customer_status", label: "Customer Status", type: "select" },
  { key: "follow_up_scheduled", label: "Follow-up Scheduled", type: "select" },
  { key: "follow_up_at", label: "Follow-up Date", type: "date" },
  { key: "products_presented", label: "Products Presented", type: "select" },

  // advanced
  { key: "custom_filters", label: "Custom Filters", type: "custom_filters" },
];

const optionsByField = {
  status: ["Pending", "Scheduled", "Ghosted", "Canceled", "Sold"],
  appointment_outcome: ["Completed", "Rebooked", "Ghosted"],
  customer_status: ["New", "In Progress", "No Show", "Sold", "Dead Deal"],
  follow_up_scheduled: ["Yes", "No"],
  products_presented: [
    "IUL",
    "Annuity",
    "Term",
    "Final Expense",
    "None",
    "Other",
  ],
  state: [
    "AL",
    "AK",
    "AZ",
    "AR",
    "CA",
    "CO",
    "CT",
    "DE",
    "FL",
    "GA",
    "HI",
    "ID",
    "IL",
    "IN",
    "IA",
    "KS",
    "KY",
    "LA",
    "ME",
    "MD",
    "MA",
    "MI",
    "MN",
    "MS",
    "MO",
    "MT",
    "NE",
    "NV",
    "NH",
    "NJ",
    "NM",
    "NY",
    "NC",
    "ND",
    "OH",
    "OK",
    "OR",
    "PA",
    "RI",
    "SC",
    "SD",
    "TN",
    "TX",
    "UT",
    "VT",
    "VA",
    "WA",
    "WV",
    "WI",
    "WY",
  ],
};

function fmtDateTimeNoSeconds(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function statusPillClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "pending") return "bg-slate-500/20 text-slate-100 ring-slate-400/25";
  if (s === "scheduled") return "bg-blue-500/20 text-blue-100 ring-blue-400/25";
  if (s === "completed") return "bg-purple-500/20 text-purple-100 ring-purple-400/25";
  if (s === "ghosted") return "bg-yellow-500/20 text-yellow-100 ring-yellow-400/25";
  if (s === "canceled" || s === "cancelled") return "bg-red-500/20 text-red-100 ring-red-400/25";
  if (s === "sold") return "bg-green-500/20 text-green-100 ring-green-400/25";
  return "bg-white/10 text-slate-100 ring-white/10";
}

function getRowField(appt, key) {
  if (!appt) return "";

  const lead = appt.lead_snapshot || {};
  const outcome = appt.summary_payload?.appointment_outcome;

  if (key === "full_name") return lead.full_name || appt.title || "Appointment";
  if (key === "phone_number") return lead.phone || lead.phone_number || "";
  if (key === "state") return lead.state || "";
  if (key === "appointment_outcome") return outcome || "";
  if (key === "start_at") return fmtDateTimeNoSeconds(appt.start_at);
  if (key === "end_at") return fmtDateTimeNoSeconds(appt.end_at);

  return appt[key] ?? "";
}

export default function AllAppointmentsCard({
  items = [],
  total = 0,
  loading = false,
  q = "",
  onChangeQ,
  limit = 25,
  onChangeLimit,
  offset = 0,
  onChangeOffset,
  sortBy,
  sortDir,
  onApplySort,
  onClearSort,
  selected,
  onToggleAll,
  onToggleRow,
  onOpenRow,

  filterOpen,
  onToggleFilterOpen,

  viewLabel = "All Appointments",
  views = [],
  onSelectView,
  onManageViews,

  tableColumns: tableColumnsProp,
  onChangeTableColumns,

  fields = APPT_FIELDS,
  optionsByField: optionsByFieldProp = optionsByField,
  filterFields,
  filterOptionsByField,
  visibleFilters,
  filtersDraft,
  onChangeFiltersDraft,
  onApplyFilters,
  onClearFilters,

  onOpenColumns,

  onDeleteSelected,
}) {
  const anySelected = (selected?.size || 0) > 0;

  const [localColumns, setLocalColumns] = React.useState(DEFAULT_TABLE_COLUMNS);
  const [columnsDialogOpen, setColumnsDialogOpen] = React.useState(false);

  const activeTableColumns = useMemo(() => {
    if (Array.isArray(tableColumnsProp) && tableColumnsProp.length > 0) return tableColumnsProp;
    return localColumns;
  }, [tableColumnsProp, localColumns]);

  const baseFieldDefs = useMemo(() => (Array.isArray(fields) && fields.length ? fields : APPT_FIELDS), [fields]);
  const filterFieldDefs = useMemo(() => {
    const extras = Array.isArray(filterFields) ? filterFields : [];
    if (extras.length === 0) return baseFieldDefs;
    const map = new Map();
    baseFieldDefs.forEach((f) => {
      if (f?.key) map.set(f.key, f);
    });
    extras.forEach((f) => {
      if (f?.key) map.set(f.key, f);
    });
    return Array.from(map.values());
  }, [baseFieldDefs, filterFields]);

  const sortedFilterFieldDefs = useMemo(() => {
    const list = Array.isArray(filterFieldDefs) ? [...filterFieldDefs] : [];
    list.sort((a, b) => {
      const aa = String(a?.label || a?.key || "").toLowerCase();
      const bb = String(b?.label || b?.key || "").toLowerCase();
      return aa.localeCompare(bb);
    });
    return list;
  }, [filterFieldDefs]);

  const mergedOptionsByField = useMemo(() => {
    return {
      ...optionsByField,
      ...(optionsByFieldProp || {}),
      ...(filterOptionsByField || {}),
    };
  }, [optionsByFieldProp, filterOptionsByField]);

  const sortedOptionsByField = useMemo(() => {
    const out = { ...(mergedOptionsByField || {}) };
    Object.keys(out).forEach((k) => {
      const vals = out[k];
      if (!Array.isArray(vals)) return;
      out[k] = [...vals].sort((a, b) => String(a || "").localeCompare(String(b || ""), undefined, { sensitivity: "base" }));
    });
    return out;
  }, [mergedOptionsByField]);

  const columnsMeta = useMemo(() => {
    const m = new Map((baseFieldDefs || []).map((f) => [f.key, f]));
    const cols = Array.isArray(activeTableColumns) && activeTableColumns.length > 0 ? activeTableColumns : DEFAULT_TABLE_COLUMNS;
    return cols.map((k) => m.get(k)).filter(Boolean);
  }, [baseFieldDefs, activeTableColumns]);

  const allOnPageSelected = items.length > 0 && selected?.size === items.length;

  const tableMinWidthPx = useMemo(() => {
    const cols = columnsMeta.length;
    return 46 + cols * 180 + 24;
  }, [columnsMeta.length]);

  return (
    <div className="flex flex-col h-full gap-3" data-testid="appointments-all-section">
      {/* Controls row on background between title and table (match Leads) */}
      <div className="flex flex-col gap-2 rounded-2xl bg-black/0 px-1 sm:flex-row sm:items-center sm:justify-between shrink-0">
        <div className="flex flex-wrap items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline" data-testid="appointments-view-selector">
                {viewLabel}
                <ChevronDown className="ml-2 h-4 w-4 opacity-80" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="glass border-white/10 text-slate-100">
              <DropdownMenuItem
                onClick={() => onSelectView?.({ id: "all", name: "All Appointments" })}
                className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
              >
                All Appointments
              </DropdownMenuItem>
              {(views || []).map((v) => (
                <DropdownMenuItem
                  key={v.id}
                  onClick={() => onSelectView?.(v)}
                  className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                >
                  {v.name}
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem
                onClick={() => onManageViews?.()}
                className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
              >
                Manage Views…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {anySelected ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="appointments-bulk-actions">
                  Actions
                  <ChevronDown className="ml-2 h-4 w-4 opacity-80" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="glass border-white/10 text-slate-100">
                <DropdownMenuItem className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                  <Download className="mr-2 h-4 w-4" /> Export (CSV)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onDeleteSelected?.()} className="text-rose-200 focus:bg-rose-500/15 focus:text-rose-100">
                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>

        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
          <CollapsibleSearch
            value={q}
            onChange={(val) => onChangeQ?.(val)}
            placeholder="Search appointments..."
            width="w-[300px]"
            data-testid="appointments-search"
          />

          {/* Filter Button - between search and columns */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onToggleFilterOpen?.()}
            aria-label="Open filters"
            data-testid="appointments-filter-icon"
            className="relative"
          >
            <ListFilter className={`h-4 w-4 ${filterOpen ? "text-indigo-400" : ""}`} />
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => {
              setColumnsDialogOpen(true);
              onOpenColumns?.();
            }}
            aria-label="Customize columns"
            data-testid="appointments-manage-columns"
          >
            <Settings2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className={"grid min-w-0 grid-cols-1 gap-3 flex-1 min-h-0 " + (filterOpen ? "lg:grid-cols-[360px_1fr]" : "lg:grid-cols-1")}>
        {filterOpen ? (
          <Card className="glass flex flex-col min-h-0 h-full" data-testid="appointments-filters-card">
            <CardContent className="h-full p-0">
              <InCardFilterPanel
                open={filterOpen}
                onOpenChange={onToggleFilterOpen}
                title="Filter Appointments by"
                // For Appointments we allow filtering by any field available in the module.
                fields={sortedFilterFieldDefs}
                optionsByField={sortedOptionsByField}
                draft={filtersDraft}
                onChangeDraft={onChangeFiltersDraft}
                onApply={() => onApplyFilters?.(filtersDraft)}
                onClear={() => onClearFilters?.()}
              />
            </CardContent>
          </Card>
        ) : null}

        <Card className="glass flex flex-col min-h-0 h-full min-w-0 w-full" data-testid="appointments-all-card">
          <CardContent className="flex h-full flex-col p-0">
            {loading ? (
              <div className="flex-1 flex items-center justify-center text-sm text-slate-200/70">
                Loading…
              </div>
            ) : (
            <div data-testid="appointments-table-wrap" className="flex-1 min-w-0 overflow-hidden">
              <div
                className="h-full w-full min-w-0 overflow-auto"
                data-testid="appointments-hscroll"
                onWheel={(e) => {
                  if (e.shiftKey) {
                    e.preventDefault();
                    e.currentTarget.scrollLeft += e.deltaY;
                  }
                }}
              >
                <Table style={{ minWidth: items.length > 0 ? tableMinWidthPx : undefined }} containerClassName="h-full">
                  <TableHeader className="relative">
                    <div className="pointer-events-none absolute left-0 right-0 bottom-0 h-px -translate-y-[4px] bg-white/10" />
                    <TableRow className="border-transparent hover:bg-transparent">
                      <TableHead colSpan={columnsMeta.length + 1} className="relative h-auto p-1 text-slate-200/70">
                        <div className="w-full px-2 py-3 grid grid-cols-[46px_1fr] items-center gap-3">
                          <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={allOnPageSelected}
                              onCheckedChange={(v) => onToggleAll?.(v)}
                              className="border-white/20 data-[state=checked]:bg-indigo-500/90 data-[state=checked]:border-indigo-400/40"
                            />
                          </div>

                          <div className="min-w-0 grid items-center gap-3 pr-2" style={{ gridTemplateColumns: `repeat(${columnsMeta.length}, minmax(160px, 1fr))` }}>
                            {columnsMeta.map((col) => (
                              <div key={col.key} className="flex min-w-0 items-center justify-between gap-2 pr-2 text-xs">
                                <span className="min-w-0 flex-1 truncate">{col.label}</span>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type="button"
                                      className={
                                        "inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-white/10 focus:outline-none" +
                                        (sortBy === col.key ? " bg-white/10" : "")
                                      }
                                      aria-label={`Sort ${col.label}`}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <ArrowUpDown className="h-3.5 w-3.5 opacity-80" />
                                    </button>
                                  </DropdownMenuTrigger>

                                  <DropdownMenuContent className="glass border-white/10 text-slate-100" align="end">
                                    <DropdownMenuItem
                                      onClick={() => onApplySort?.(col.key, "asc")}
                                      className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                                    >
                                      <ArrowDownAZ className="mr-2 h-4 w-4" /> Sort Ascending
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => onApplySort?.(col.key, "desc")}
                                      className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                                    >
                                      <ArrowUpAZ className="mr-2 h-4 w-4" /> Sort Descending
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => onClearSort?.()}
                                      className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                                    >
                                      Clear sort
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            ))}
                          </div>
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {items.length === 0 ? (
                      <TableRow className="border-transparent">
                        <TableCell colSpan={columnsMeta.length + 1} className="py-16">
                          <div className="sticky left-1/2 -translate-x-1/2 w-max flex flex-col items-center justify-center">
                            <Contact className="h-12 w-12 text-indigo-400 mb-3" />
                            <div className="text-sm text-slate-200/70">No appointments found.</div>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : items.map((appt) => (
                        <TableRow key={appt.id} className="border-transparent">
                          <TableCell colSpan={columnsMeta.length + 1} className="p-1 align-top">
                            <div
                              role="button"
                              tabIndex={0}
                              className="w-full rounded-xl bg-white/5 px-2 py-3 text-left hover:bg-white/10 hover:ring-1 hover:ring-white/10"
                              onClick={() => onOpenRow?.(appt)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  onOpenRow?.(appt);
                                }
                              }}
                            >
                              <div className="grid grid-cols-[46px_1fr] items-center gap-3">
                                <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                                  <Checkbox
                                    checked={selected?.has(appt.id)}
                                    onCheckedChange={() => onToggleRow?.(appt.id)}
                                    className="border-white/20 data-[state=checked]:bg-indigo-500/90 data-[state=checked]:border-indigo-400/40"
                                  />
                                </div>

                                <div className="min-w-0 grid items-center gap-3 pr-2" style={{ gridTemplateColumns: `repeat(${columnsMeta.length}, minmax(160px, 1fr))` }}>
                                  {columnsMeta.map((col) => {
                                    const val = getRowField(appt, col.key);
                                    if (col.key === "status") {
                                      return (
                                        <div key={col.key} className="min-w-0 pr-2">
                                          <span className={"inline-flex rounded-full px-3 py-1 text-xs font-medium ring-1 " + statusPillClass(val)}>
                                            {String(val || "—")}
                                          </span>
                                        </div>
                                      );
                                    }

                                    return (
                                      <div key={col.key} className="min-w-0 pr-2">
                                        <div className="truncate text-slate-100/90">{String(val || "—")}</div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    }
                  </TableBody>
                </Table>
              </div>
            </div>
            )}

            <div data-testid="appointments-footer" className="h-[60px] border-t border-white/10 px-4 flex items-center justify-between shrink-0">
              <div className="text-xs text-slate-200/70">{anySelected ? `Selected: ${selected.size}` : `Total Appointments: ${total}`}</div>

              <div className="flex flex-wrap items-center gap-2">
                {offset > 0 ? (
                  <Button
                    variant="secondary"
                    className="rounded-full bg-white/15 text-slate-100 ring-1 ring-white/20 hover:bg-white/20"
                    onClick={() => onChangeOffset?.(Math.max(0, offset - limit))}
                  >
                    Prev
                  </Button>
                ) : null}

                {offset + limit < total ? (
                  <Button
                    variant="secondary"
                    className="rounded-full bg-white/15 text-slate-100 ring-1 ring-white/20 hover:bg-white/20"
                    onClick={() => onChangeOffset?.(offset + limit)}
                  >
                    Next
                  </Button>
                ) : null}

                <Select value={String(limit)} onValueChange={(v) => onChangeLimit?.(Number(v))}>
                  <SelectTrigger className="h-9 w-[110px] rounded-full bg-white/15 text-slate-100 ring-1 ring-white/20" data-testid="appointments-page-size-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="glass border-white/10">
                    {[10, 25, 50, 100].map((n) => (
                      <SelectItem key={n} value={String(n)} data-testid={`appointments-page-size-${n}`}>
                        {n} / Page
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <AppointmentTableColumnsDialog
        open={columnsDialogOpen}
        onOpenChange={setColumnsDialogOpen}
        fields={fields}
        value={activeTableColumns}
        onSave={(cols) => {
          setLocalColumns(cols);
          onChangeTableColumns?.(cols);
        }}
      />
    </div>
  );
}
